import { IconButton, UIText3 } from '@cian/ui-kit';
import { Attention16 } from '@cian/ui-kit/prev';
import * as React from 'react';

import {
  useMessageHtml,
  useMessageText,
  useImagesNode,
  useImageAsDocument,
  useDocument,
  useUserName,
  useOutcomingMessageStyle,
} from './hooks';
import { getStatusIcon } from './utils';
import { ChatMessageLayout } from '../../components/ChatMessageLayout';
import { MessageErrorMenu } from '../../components/MessageErrorMenu';
import { ERequestApiStatus } from '../../types/appState';
import { TMessage, TOutcomingStatus } from '../../types/messages';
import { shouldShowDailyrentReviewButtons } from '../../utils/dailyrent/shouldShowDailyrentReviewButtons';
import { ActionButtonContainer } from '../ActionButtonContainer';
import { BubbleMessageContainer } from '../BubbleMessageContainer';
import { DocumentMessageContainer } from '../DocumentMessageContainer';
import { ImageAsDocumentMessageContainer } from '../ImageAsDocumentMessageContainer';
import { InfoMessageContainer } from '../InfoMessageContainer';
import { OfferMessageContainer } from '../OfferMessageContainer';

type TChatMessageContainerProps = {
  message: TMessage & { status: TOutcomingStatus };
  messageId: string;
  isLastInChain: boolean;
  isFirstUserPhrase: boolean;
};

export const ChatMessageContainer: React.FC<TChatMessageContainerProps> = ({
  message,
  messageId,
  isLastInChain,
  isFirstUserPhrase,
}) => {
  const [showErrorMenu, setShowErrorMenu] = React.useState(false);
  const {
    message: text,
    htmlText,
    created,
    status,
    files,
    actionButtons,
    isRead,
    assumptions,
    bubbles,
    userId,
    isOutcoming,
  } = message;
  const isOfferMessage = React.useMemo(() => Boolean(message?.offers?.length), [message]);
  const buttonRef = React.createRef<HTMLButtonElement>();

  const formattedText = useMessageText(text);
  const formattedHtmlText = useMessageHtml(htmlText);

  const isMessageLoading = status === ERequestApiStatus.Pending;
  const isMessageFailed = status === ERequestApiStatus.Failed;

  const imagesNode = useImagesNode(files, messageId, isMessageLoading, isMessageFailed);
  const { imageAsDocument, imageAsDocumentDraft } = useImageAsDocument(messageId, files?.imagesAsDocuments?.[0]);
  const { document, documentDraft } = useDocument(messageId, files?.documents?.[0]);

  const { userName, nameReplacementImageUrl } = useUserName({
    messageUserId: userId,
    isFirstUserPhrase,
    withImage: Boolean(imagesNode || imageAsDocument || imageAsDocumentDraft),
    withOffer: isOfferMessage,
  });

  const handleSetShowErrorMenu = React.useCallback(() => {
    setShowErrorMenu(!showErrorMenu);
  }, [showErrorMenu]);

  const hasText = formattedText || formattedHtmlText;
  const isOnlyImage = Boolean(!hasText && imagesNode);
  const isTextAndImage = Boolean(hasText && imagesNode);
  const isUnread = !isOutcoming && !isRead;
  const isOutcomingMessageStyle = useOutcomingMessageStyle(isOutcoming, userId);
  const showDailyrentReviewButtons = shouldShowDailyrentReviewButtons(actionButtons);

  // eslint-disable-next-line react/no-danger
  const textNode = formattedHtmlText ? <div dangerouslySetInnerHTML={{ __html: formattedHtmlText }} /> : formattedText;
  const actionButtonsNode = actionButtons?.length
    ? actionButtons.map(actionButton => (
        <ActionButtonContainer key={actionButton.text} actionButton={actionButton} messageId={messageId} />
      ))
    : null;
  const statusNode = isOutcoming && !isMessageFailed ? getStatusIcon(status) : null;
  const errorSignNode = isMessageFailed ? (
    <IconButton ref={buttonRef} data-name="ErrorSignNode" icon={<Attention16 />} onClick={handleSetShowErrorMenu} />
  ) : null;
  const errorTextNode = isMessageFailed ? <UIText3 color="error_100">не отправлено</UIText3> : null;
  const errorMenu = showErrorMenu ? (
    <MessageErrorMenu
      buttonRef={buttonRef}
      isOfferMessage={isOfferMessage}
      messageId={messageId}
      closeMenu={handleSetShowErrorMenu}
    />
  ) : null;
  const infoMessageNode = <InfoMessageContainer isOutcoming={isOutcoming} assumptions={assumptions} />;
  const documentNode =
    document || documentDraft ? (
      <DocumentMessageContainer
        isOutcoming={isOutcoming}
        document={document}
        documentDraft={documentDraft}
        isMessageLoading={isMessageLoading}
        isMessageFailed={isMessageFailed}
        messageId={messageId}
      />
    ) : null;
  const imageAsDocumentNode =
    imageAsDocument || imageAsDocumentDraft ? (
      <ImageAsDocumentMessageContainer
        isOutcoming={isOutcoming}
        imageAsDocument={imageAsDocument}
        imageAsDocumentDraft={imageAsDocumentDraft}
        isMessageLoading={isMessageLoading}
        isMessageFailed={isMessageFailed}
        messageId={messageId}
      />
    ) : null;

  const offerNode = (
    <OfferMessageContainer
      isMessageFailed={isMessageFailed}
      messageId={messageId}
      message={message}
      isOutcoming={isOutcoming}
    />
  );

  const bubblesNode = bubbles?.length
    ? bubbles.map(bubble => <BubbleMessageContainer key={bubble.title.label} bubble={bubble} messageId={messageId} />)
    : null;

  // FIXME рефакторинг https://jira.cian.tech/browse/ACTI-100 разбить на разные типы баблов, вынести общую обертку со стилями, хвостиками
  return (
    <ChatMessageLayout
      messageUserId={userId}
      isOutcoming={isOutcomingMessageStyle}
      isUnread={isUnread}
      isOnlyImage={isOnlyImage}
      isOffer={isOfferMessage}
      isLastInChain={isLastInChain}
      isTextAndImage={isTextAndImage}
      created={created}
      imagesNode={imagesNode}
      textNode={textNode}
      actionButtonsNode={actionButtonsNode}
      statusNode={statusNode}
      errorSignNode={errorSignNode}
      errorTextNode={errorTextNode}
      errorMenuNode={errorMenu}
      infoMessageNode={infoMessageNode}
      documentNode={documentNode}
      imageAsDocumentNode={imageAsDocumentNode}
      offerNode={offerNode}
      bubblesNode={bubblesNode}
      userName={userName}
      nameReplacementImageUrl={nameReplacementImageUrl}
      showDailyrentReviewButtons={showDailyrentReviewButtons}
    />
  );
};
